import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Policy as PolicyIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import PolicyModelCreator from './PolicyModelCreator';
import PolicyModelTester from './PolicyModelTester';
import DecisionTreeEditor from './DecisionTreeEditor';
import DecisionTreeEditorReactFlow from './DecisionTreeEditorReactFlow';

function PolicyModelTab({ preselectedDocument = null, preselectedCollection = null }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const [modelToEdit, setModelToEdit] = useState(null);
  const [useReactFlowEditor, setUseReactFlowEditor] = useState(true); // Toggle between editors

  // Auto-open create dialog if document is preselected
  useEffect(() => {
    if (preselectedDocument) {
      setCreateDialogOpen(true);
    }
  }, [preselectedDocument]);

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/v1/policy/models');
      if (response.ok) {
        const data = await response.json();
        setModels(data.models || []);
      } else {
        setError('Failed to load policy models');
      }
    } catch (err) {
      setError(`Error loading models: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    loadModels();
  };

  const handleTestModel = (model) => {
    setSelectedModel(model);
    setTestDialogOpen(true);
  };

  const handleDeleteModel = (model) => {
    setModelToDelete(model);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!modelToDelete) return;

    try {
      const response = await fetch(`/api/v1/policy/models/${modelToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        loadModels();
        setDeleteDialogOpen(false);
        setModelToDelete(null);
      } else {
        setError('Failed to delete model');
      }
    } catch (err) {
      setError(`Error deleting model: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
            <PolicyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Policy Models
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage decision trees generated from policy documents
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateModel}
          sx={{ borderRadius: 2 }}
        >
          Create Model
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : models.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 8 }}>
          <CardContent>
            <PolicyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No policy models yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first policy model from an uploaded document
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateModel}
            >
              Create Model
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Source Document</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Queries</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.map((model) => (
                <TableRow key={model.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PolicyIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {model.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {model.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={model.source_document?.filename || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(model.metadata?.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {model.stats?.total_queries || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Edit Tree">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setModelToEdit(model);
                            setEditorDialogOpen(true);
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Test Model">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleTestModel(model)}
                        >
                          <TestIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Model">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteModel(model)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Model Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Policy Model</DialogTitle>
        <DialogContent>
          <PolicyModelCreator
            onSuccess={handleCreateSuccess}
            onCancel={() => setCreateDialogOpen(false)}
            preselectedDocument={preselectedDocument}
            preselectedCollection={preselectedCollection}
          />
        </DialogContent>
      </Dialog>

      {/* Test Model Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Test Policy Model: {selectedModel?.name}</DialogTitle>
        <DialogContent>
          {selectedModel && (
            <PolicyModelTester
              model={selectedModel}
              onClose={() => setTestDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Policy Model</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{modelToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tree Editor Dialog */}
      <Dialog
        open={editorDialogOpen}
        onClose={() => setEditorDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {modelToEdit && (
            useReactFlowEditor ? (
              <DecisionTreeEditorReactFlow
                model={modelToEdit}
                onSave={() => {
                  loadModels();
                  setEditorDialogOpen(false);
                }}
                onClose={() => setEditorDialogOpen(false)}
              />
            ) : (
              <DecisionTreeEditor
                model={modelToEdit}
                onSave={() => {
                  loadModels();
                  setEditorDialogOpen(false);
                }}
                onClose={() => setEditorDialogOpen(false)}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default PolicyModelTab;

